// src/components/network/connections/ConnectionStatus.jsx
import React, { useState } from 'react';
import { UserPlus, X, Check, UserCheck, Clock } from 'lucide-react';
import { db } from '../../../firebase/config';
import { collection, doc, setDoc, deleteDoc, updateDoc,
query, where, getDocs,
serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';

const ConnectionStatus = ({ businessId, status = 'none', onStatusChange }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Send connection request
  const handleSendRequest = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      // Create a new connection document
      const connectionId = uuidv4();

      await setDoc(doc(db, 'connections', connectionId), {
        requesterId: currentUser.uid,
        receiverId: businessId,
        status: 'pending',
        createdAt: serverTimestamp(),
        participants: [currentUser.uid, businessId]
      });

      // Update local state via callback
      if (onStatusChange) {
        onStatusChange('pending');
      }

    } catch (error) {
      console.error('Error sending connection request:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cancel connection request
  const handleCancelRequest = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const connectionsRef = collection(db, 'connections');
      const q = query(
        connectionsRef,
        where('requesterId', '==', currentUser.uid),
        where('receiverId', '==', businessId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const connectionDoc = querySnapshot.docs[0];
        await deleteDoc(doc(db, 'connections', connectionDoc.id));
      }

      // Update local state via callback
      if (onStatusChange) {
        onStatusChange('none');
      }

    } catch (error) {
      console.error('Error canceling connection request:', error);
    } finally {
      setLoading(false);
    }
  };

  // Accept a received connection request
  const handleAcceptRequest = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const connectionsRef = collection(db, 'connections');
      const q = query(
        connectionsRef,
        where('requesterId', '==', businessId),
        where('receiverId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const connectionDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'connections', connectionDoc.id), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        });
      }

      if (onStatusChange) {
        onStatusChange('accepted');
      }
    } catch (error) {
      console.error('Error accepting connection request:', error);
    } finally {
      setLoading(false);
    }
  };

  // Decline a received connection request
  const handleDeclineRequest = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const connectionsRef = collection(db, 'connections');
      const q = query(
        connectionsRef,
        where('requesterId', '==', businessId),
        where('receiverId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const connectionDoc = querySnapshot.docs[0];
        await deleteDoc(doc(db, 'connections', connectionDoc.id));
      }

      if (onStatusChange) {
        onStatusChange('none');
      }
    } catch (error) {
      console.error('Error declining connection request:', error);
    } finally {
      setLoading(false);
    }
  };

  const spinner = (
    <div className="animate-spin h-4 w-4 border-2 border-current rounded-full border-t-transparent mr-2"></div>
  );

  // Connected state
  if (status === 'accepted' || status === 'connected') {
    return (
      <button
        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg flex items-center"
        disabled
      >
        <UserCheck size={18} className="mr-2" />
        Connected
      </button>
    );
  }

  // Pending received - show Accept/Decline buttons
  if (status === 'pending_received') {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={handleAcceptRequest}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
        >
          {loading ? spinner : <Check size={18} className="mr-2" />}
          Accept
        </button>
        <button
          onClick={handleDeclineRequest}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
        >
          <X size={18} className="mr-2" />
          Decline
        </button>
      </div>
    );
  }

  // Pending sent - show Cancel button
  if (status === 'pending') {
    return (
      <button
        onClick={handleCancelRequest}
        disabled={loading}
        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
      >
        {loading ? spinner : <Clock size={18} className="mr-2" />}
        Pending
      </button>
    );
  }

  // Default - show Connect button
  return (
    <button
      onClick={handleSendRequest}
      disabled={loading}
      className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] flex items-center"
    >
      {loading ? spinner : <UserPlus size={18} className="mr-2" />}
      Connect
    </button>
  );
};

export default ConnectionStatus;
